import { useState, type ReactNode } from 'react';
import { Collapse, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';

export interface EditAccordionTab {
  id: string;
  label: string;
  content: ReactNode;
}

export interface EditAccordionProps {
  isOpen: boolean;
  onClose: () => void;
  /** bootstrap-icons class for the head icon (e.g. "bi-building"). */
  icon?: string;
  /** mono code shown in the head (e.g. warehouse code / user email). */
  code?: string;
  /** primary title in the head (name). */
  title: string;
  /** optional badges rendered next to the title. */
  badges?: ReactNode;
  /** optional sub-line under the title (meta). */
  sub?: ReactNode;
  tabs: EditAccordionTab[];
  /** sticky footer content (action buttons). */
  footer?: ReactNode;
}

/**
 * Master-detail edit panel (SP-5m). The shared "edit-accordion" used by ALL
 * list pages except Pick-list: a row expands inline into this panel with a
 * head, tabbed content and a sticky footer. Embed it in a table via
 * `<tr><td colSpan={n} className="p-0 border-0"><EditAccordion …/></td></tr>`,
 * or standalone for "create".
 */
export function EditAccordion({ isOpen, onClose, icon, code, title, badges, sub, tabs, footer }: EditAccordionProps) {
  const [active, setActive] = useState(tabs[0]?.id ?? '');
  const activeId = tabs.some((t) => t.id === active) ? active : (tabs[0]?.id ?? '');

  return (
    <Collapse isOpen={isOpen}>
      <div className="card border-0 border-start border-3 border-primary shadow-sm my-2">
        {/* Head */}
        <div className="card-header bg-body d-flex align-items-center gap-3">
          {icon && (
            <span className="avatar-xs d-inline-flex align-items-center justify-content-center rounded bg-primary-subtle text-primary fs-5">
              <i className={`bi ${icon}`} />
            </span>
          )}
          <div className="min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {code && <span className="font-monospace vp-code">{code}</span>}
              <span className="fw-medium text-truncate">{title}</span>
              {badges}
            </div>
            {sub && <div className="text-muted small mt-1">{sub}</div>}
          </div>
          <div className="ms-auto">
            <button type="button" className="btn btn-sm btn-ghost-secondary" aria-label="Chiudi" onClick={onClose}>
              <i className="bi bi-x-lg" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        {tabs.length > 1 && (
          <Nav tabs className="px-3 pt-2">
            {tabs.map((t) => (
              <NavItem key={t.id}>
                <NavLink
                  href="#"
                  active={activeId === t.id}
                  onClick={(e) => { e.preventDefault(); setActive(t.id); }}
                >
                  {t.label}
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        )}

        <div className="card-body">
          <TabContent activeTab={activeId}>
            {tabs.map((t) => (
              <TabPane key={t.id} tabId={t.id}>
                {t.content}
              </TabPane>
            ))}
          </TabContent>
        </div>

        {footer && (
          <div className="card-footer bg-body position-sticky bottom-0 d-flex justify-content-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </Collapse>
  );
}
